import { sectionRenderer } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

class RecentlyViewedSection extends HTMLElement {
  connectedCallback() {
    this.#render();
  }

  #notifyTrackingRefresh() {
    document.dispatchEvent(new Event('recently-viewed:rendered'));
  }

  /**
   * @param {string} baseURL
   * @param {string[]} viewedProducts
   */
  #buildQueryURL(baseURL, viewedProducts) {
    const url = new URL(baseURL, location.origin);
    url.searchParams.delete('section_id');
    url.searchParams.delete('page');
    url.searchParams.set('q', viewedProducts.map((/** @type {string} */ id) => `id:${id}`).join(' OR '));
    url.searchParams.set('type', 'product');
    url.searchParams.set('resources[type]', 'product');
    return url;
  }

  /**
   * @param {string[]} viewedProducts
   */
  #buildSearchSectionURL(viewedProducts) {
    const url = this.#buildQueryURL(Theme.routes.search_url, viewedProducts);
    // Render this section by section type when section instance rendering is unavailable on /search.
    url.searchParams.set('section_id', 'recently-viewed-products');
    return url;
  }

  async #render() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const currentProductId = this.dataset.productId;
    const limit = Math.max(parseInt(this.dataset.limit || '4', 10) || 4, 1);

    // Request limit + 1 to account for current product being filtered out.
    const viewedProducts = RecentlyViewed.getProducts()
      .slice(0, limit + 1)
      .filter((id) => id && id !== currentProductId)
      .slice(0, limit);

    if (!viewedProducts.length) {
      this.hidden = true;
      return;
    }

    try {
      const urlsToTry = [
        this.#buildQueryURL(window.location.href, viewedProducts),
        this.#buildQueryURL(Theme.routes.search_url, viewedProducts),
      ];

      for (const url of urlsToTry) {
        const sectionHTML = await sectionRenderer.getSectionHTML(sectionId, false, url);
        const parsed = new DOMParser().parseFromString(sectionHTML, 'text/html');
        const incomingSection = parsed.getElementById(`recently-viewed-products-${sectionId}`);
        const incomingResults =
          incomingSection?.querySelector('[data-recently-viewed-results]') ??
          parsed.querySelector('[data-recently-viewed-results]');

        const hasProducts = incomingResults?.getAttribute('data-has-products') === 'true';
        if (!hasProducts || !(incomingResults instanceof HTMLElement)) {
          continue;
        }

        if (incomingSection instanceof HTMLElement) {
          this.innerHTML = incomingSection.innerHTML;
          this.hidden = false;
          this.#notifyTrackingRefresh();
          return;
        }

        const currentResults = this.querySelector('[data-recently-viewed-results]');
        if (currentResults instanceof HTMLElement) {
          currentResults.innerHTML = incomingResults.innerHTML;
          currentResults.setAttribute('data-has-products', 'true');
          this.hidden = false;
          this.#notifyTrackingRefresh();
          return;
        }
      }

      // Final fallback: fetch by section type from search route.
      const fallbackResponse = await fetch(this.#buildSearchSectionURL(viewedProducts).toString());
      if (fallbackResponse.ok) {
        const html = await fallbackResponse.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const incomingResults = parsed.querySelector('[data-recently-viewed-results]');
        const hasProducts = incomingResults?.getAttribute('data-has-products') === 'true';

        if (incomingResults instanceof HTMLElement && hasProducts) {
          const currentResults = this.querySelector('[data-recently-viewed-results]');
          if (currentResults instanceof HTMLElement) {
            currentResults.innerHTML = incomingResults.innerHTML;
            currentResults.setAttribute('data-has-products', 'true');
            this.hidden = false;
            this.#notifyTrackingRefresh();
            return;
          }
        }
      }

      this.hidden = true;
    } catch (error) {
      this.hidden = true;
    }
  }
}

if (!customElements.get('recently-viewed-section')) {
  customElements.define('recently-viewed-section', RecentlyViewedSection);
}
